import { Alert, Card, Col, Row, Typography } from "antd"
import { LayoutLoading } from "components"
import styled from "styled-components"
import { ArrowRightOutlined, ShoppingOutlined } from "@ant-design/icons"

const Section = styled.section`
  background: var(--background-secondary, #fafafa);
  border-radius: 8px;
  padding: 24px;
  border: 1px solid var(--borders, #f0f0f0);
`

const SectionHeader = styled.div`
  margin-bottom: 24px;
`

const ProductGrid = styled(Row)`
  margin: 0 -8px;
  .ant-col {
    padding: 0 8px 16px;
  }
`

const ProductBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  .ant-card-meta {
    flex: 1;
    margin-bottom: 0;
  }
`

const StartQuoteIcon = styled.span`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: var(--ant-primary-color, rgb(121, 117, 190));
  background: var(--background-secondary, #fafafa);
  font-size: 16px;
  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;
`

const ProductCard = styled(Card)`
  height: 100%;
  border-radius: 8px;
  border: 1px solid var(--borders, #f0f0f0);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: var(--ant-primary-color, rgb(121, 117, 190));
    transform: translateY(-2px);
  }

  &:hover ${StartQuoteIcon} {
    color: #ffffff;
    background: var(--ant-primary-color, rgb(121, 117, 190));
    transform: translateX(2px);
  }

  .ant-card-body {
    padding: 20px;
  }

  .ant-card-meta-title {
    font-weight: 600;
    margin-bottom: 4px;
  }

  .ant-card-meta-description {
    color: rgba(0, 0, 0, 0.55);
    font-size: 13px;
  }
`

const ProductCover = styled.div`
  height: 120px;
  overflow: hidden;
  border-radius: 8px 8px 0 0;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const ProductGroup = styled.div`
  & + & {
    margin-top: 32px;
  }
`

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--borders, #f0f0f0);
`

const ChooseProduct: React.FC<IChooseProductProps> = ({
  onChooseProduct,
  activeProducts = [],
  groups,
  isLoading = false,
  title = "Choose a product",
  description = "Select a product below to start your quote.",
  icon = <ShoppingOutlined />,
  ...props
}) => {
  if (isLoading) {
    return <LayoutLoading />
  }

  const productGroups = groups ?? [{ products: activeProducts }]
  const populatedGroups = productGroups.filter(
    (group) => group.products.length > 0
  )

  const renderGrid = (products: IActiveProduct[]) => (
    <ProductGrid gutter={0}>
      {products.map((item) => (
        <Col key={item.productReferenceId} xs={24} sm={12} md={8} lg={6}>
          <ProductCard
            hoverable
            onClick={() => onChooseProduct(item)}
            cover={
              item.bannerImage ? (
                <ProductCover>
                  <img src={item.bannerImage} alt="" />
                </ProductCover>
              ) : undefined
            }
          >
            <ProductBody>
              <Card.Meta title={item.label} description={item.desc} />
              <StartQuoteIcon
                aria-label={`Start a quote for ${item.label}`}
                role="img"
              >
                <ArrowRightOutlined />
              </StartQuoteIcon>
            </ProductBody>
          </ProductCard>
        </Col>
      ))}
    </ProductGrid>
  )

  return (
    <Section>
      <SectionHeader>
        <Typography.Title
          level={4}
          style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}
        >
          {icon}
          {title}
        </Typography.Title>
        <Typography.Text
          type="secondary"
          style={{ display: "block", marginTop: 4 }}
        >
          {description}
        </Typography.Text>
      </SectionHeader>

      {populatedGroups.length === 0 ? (
        <Alert
          type="info"
          showIcon
          message="Sorry, there are no products available at the moment."
          style={{ marginBottom: 0 }}
        />
      ) : (
        populatedGroups.map((group, index) => (
          <ProductGroup key={group.title ?? index}>
            {group.title && (
              <GroupHeader>
                {group.icon}
                <Typography.Title level={5} style={{ margin: 0 }}>
                  {group.title}
                </Typography.Title>
              </GroupHeader>
            )}
            {renderGrid(group.products)}
          </ProductGroup>
        ))
      )}
    </Section>
  )
}

interface IProductGroup {
  title?: string
  icon?: React.ReactNode
  products: IActiveProduct[]
}

interface IChooseProductProps {
  onChooseProduct: (product: IActiveProduct) => void
  activeProducts?: IActiveProduct[]
  groups?: IProductGroup[]
  isLoading?: boolean
  title?: string
  description?: string
  icon?: React.ReactNode
}

export interface IActiveProduct {
  id: string | number
  callToActionLabel?: string
  desc?: string
  label?: string
  labelIcon?: string
  productReferenceId: string
  bannerImage?: string
  productType?: string
  dashboardOrder: number
  isActive: boolean
  name: string
  hasQuickQuote?: boolean
  coverType?: string
}

export type QuoteTypeCreation = "quickquote" | "quote"

export default ChooseProduct
